import { parseDate } from '@blackhyve/utilities/dates';
import { formatTaskToGanttTask } from '@blackhyve/utilities/gantt';
import { calculateTaskForecastedDates } from '@blackhyve/utilities/tasks';
import { Box, CircularProgress, Fade } from '@mui/material';
import DisplayWithEditAccess from 'components/common/v3/DisplayWithEditAccess';
import { initGanttColumns } from 'components/projectOverview/gantt/ganttConfig/column/columnConfig';
import { drawFloatLayer } from 'components/projectOverview/gantt/ganttConfig/float';
import { addDays, differenceInDays, isValid } from 'date-fns';
import { fetchTrades } from 'features/companies/store/companiesSlice';
import Gantt from 'features/gantt/components/Gantt';
import { addForecastedDatesLayer } from 'features/gantt/layers/addForecastedDatesLayer';
import { addWeekdaysLayer } from 'features/gantt/layers/addWeekdaysLayer';
import { taskGanttDataProcessor } from 'features/tasksGantt/utils/gantt/taskGanttDataProcessor';
import {
  getCalendarDays,
  taskGanttEvents,
  taskGanttInlineEvents,
} from 'features/tasksGantt/utils/gantt/taskGanttEvents';
import { drawCompanyColorLayer } from 'features/tasksGantt/utils/gantt/taskGanttLayers';
import initMakers from 'features/tasksGantt/utils/gantt/taskGanttMarkers';
import { taskGanttRenderer } from 'features/tasksGantt/utils/gantt/taskGanttRenderer';
import { templates } from 'features/tasksGantt/utils/gantt/taskGanttTemplates';
import { zoomConfig } from 'features/tasksGantt/utils/gantt/zoomConfig';
import { useCanEditProject } from 'hooks/useCanEditProject';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCustomViews, selectCustomViewEntities } from 'slices/customViewSlice';
import EditPredecessorDialog from '../../../components/projectOverview/gantt/components/dialog/EditPredecessorDialog';
import BulkEditMenu from '../../../components/projectOverview/gantt/ganttConfig/column/menu/BulkEditMenu';
import ganttStore from '../../../components/projectOverview/gantt/ganttConfig/ganttStore';
import { selectAllDependencies } from '../../dependencies/store/dependenciesSlice';
import PlanningBoardTaskDrawer from '../../drawers/components/PlanningBoardTaskDrawer';
import RecipeDrawer from '../../drawers/components/RecipeDrawer';
import { fetchLocations } from '../../locations/store/locationsSlice';
import { fetchProject, selectProjectById } from '../../projects/store/projectsSlice';
import {
  fetchTasks,
  selectAllPlanningTasks,
  setPredecessorDialog,
} from '../../tasks/store/tasksSlice';
/**
 * Base schedule component
 * @param {*} props
 * @param {Number} props.projectId
 * @return {JSX}
 */
const PlanningGantt = ({ projectId, ganttId, reloadGantt }) => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(true);
  const tasks = useSelector((state) => selectAllPlanningTasks(state)).map((task) =>
    formatTaskToGanttTask(task)
  );
  const dependencies = useSelector((state) => selectAllDependencies(state));
  const project = useSelector((state) => selectProjectById(state, projectId));
  const predecessorDialog = useSelector((state) => state.tasks.predecessorDialog);
  const canEdit = useCanEditProject(projectId);
  const customViewEntities = useSelector((state) => selectCustomViewEntities(state));
  const selectedCustomViewId = useSelector((state) => state.customViews?.selected);
  const selectedCustomView = customViewEntities[selectedCustomViewId];

  // const newTasks = tasks.map((task) => {
  //   if (selectedCustomView?.parameters?.closedTask?.includes(task.id)) {
  //     task.$open = false;
  //   }
  //   return task;
  // });

  const gantt = ganttStore.createGantt(ganttId);

  const handleClosePredecessorDialog = () => {
    dispatch(setPredecessorDialog({ taskId: null, open: false }));
  };

  useEffect(() => {
    if (project && !loading && gantt?.isTaskExists(-1)) {
      const projectStartDate = parseDate(project?.start_date);
      const projectForecastedEndDate =
        project?.est_completion_date && addDays(parseDate(project?.est_completion_date), 1);
      const projectScheduledEndDate =
        project?.sub_completion_date && addDays(parseDate(project?.sub_completion_date), 1);

      const projectTask = gantt.getTask(-1);
      let isCalendarUpdated = false;
      const projectCalendarDays = getCalendarDays(project.workday_calendar);
      const globalCalendarDays = gantt.getCalendar('global')._worktime.dates;
      projectCalendarDays.forEach((day, index) => {
        if (globalCalendarDays[index] !== Boolean(day)) {
          isCalendarUpdated = true;
          return false;
        }
      });

      if (isCalendarUpdated) {
        gantt.addCalendar({
          id: 'global',
          worktime: {
            days: projectCalendarDays,
          },
        });
        gantt.batchUpdate(function () {
          gantt.eachTask(function (task) {
            if (task.type === gantt.config.types.placeholder || task.type !== 'todo') {
              return;
            }
            const newStartDate = gantt.getClosestWorkTime({
              dir: 'future',
              date: task.start_date,
              unit: gantt.config.duration_unit,
              task: task,
            });
            task.start_date = newStartDate;
            task.end_date = gantt.calculateEndDate({
              start_date: newStartDate,
              duration: task.duration,
              task: task,
            });
            task.duration_string = task.duration;
            task.cal_days = differenceInDays(task.end_date, task.start_date);
            gantt.updateTask(task.id);
          });
        });
      }

      projectTask.text = project?.name;
      projectTask.start_date = parseDate(project?.start_date);
      projectTask.end_date = isValid(projectForecastedEndDate)
        ? projectForecastedEndDate
        : projectScheduledEndDate;
      projectTask.scheduled_end_date = projectScheduledEndDate;
      projectTask.forecasted_start_date = projectStartDate;
      projectTask.forecasted_end_date = projectForecastedEndDate;
      projectTask.work_days = gantt.calculateDuration(projectStartDate, projectTask.end_date);
      projectTask.cal_days = differenceInDays(projectTask.end_date, projectStartDate);
      gantt.refreshData();
      calculateTaskForecastedDates(projectTask, gantt);
    }
  }, [projectId, project, loading, gantt]);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      dispatch(fetchProject(projectId)),
      dispatch(fetchTasks({ projectId, includes: 'latestJobWalk' })),
      dispatch(fetchLocations({ id: projectId })),
      dispatch(fetchTrades()),
      dispatch(fetchCustomViews('schedule')),
    ])
      .then(() => {
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [dispatch, projectId]);

  const projectStartDate = parseDate(project?.start_date);
  const projectScheduledEndDate = addDays(parseDate(project?.sub_completion_date), 1);
  const projectForecastedEndDate = project?.est_completion_date
    ? addDays(parseDate(project?.est_completion_date), 1)
    : projectScheduledEndDate;

  const getGanttColumn = () => {
    const columnEntities = initGanttColumns(ganttId);
    return [
      columnEntities.action,
      columnEntities.wbs,
      columnEntities.name,
      columnEntities.uuid,
      columnEntities.status,
      columnEntities.calendarDays,
      columnEntities.workingDays,
      columnEntities.startDate,
      columnEntities.endDate,
      columnEntities.forecastedStartDate,
      columnEntities.forecastedEndDate,
      columnEntities.actualStartDate,
      columnEntities.actualEndDate,
      columnEntities.effortHours,
      columnEntities.predecessor,
      columnEntities.successorColumn,
      columnEntities.crewSize,
      columnEntities.location,
      columnEntities.zone,
      columnEntities.area,
      columnEntities.companies,
      columnEntities.contacts,
      columnEntities.responsible,
      columnEntities.trades,
      columnEntities.constraintType,
      columnEntities.constraintDate,
      columnEntities.totalSlack,
      columnEntities.freeSlack,
      columnEntities.progress,
      columnEntities.pace,
      columnEntities.earned_days,
      columnEntities.earned_planned_days,
      columnEntities.pr,
    ];
  };

  return (
    <>
      {!reloadGantt && !loading && tasks && dependencies && gantt && project ? (
        <div style={{ height: '100%' }}>
          <Gantt
            columns={getGanttColumn()}
            ganttId={ganttId}
            initDataProcessor={taskGanttDataProcessor}
            initMakers={initMakers}
            inlineEvents={taskGanttInlineEvents}
            templates={templates}
            typeRenders={{ task: taskGanttRenderer, project: taskGanttRenderer }}
            zoomConfig={zoomConfig}
            config={{
              project_bar: true,
              sort: true,
              auto_types: false,
              readonly: !canEdit,
              placeholder_task: canEdit,
              fit_tasks: false,
              start_date: addDays(projectStartDate, -7),
              end_date: addDays(
                +projectForecastedEndDate < +projectScheduledEndDate
                  ? projectScheduledEndDate
                  : projectForecastedEndDate,
                7
              ),
              initial_scroll: false,
            }}
            constants={{
              start_date: addDays(projectStartDate, -7),
              end_date: addDays(
                +projectForecastedEndDate < +projectScheduledEndDate
                  ? projectScheduledEndDate
                  : projectForecastedEndDate,
                7
              ),
            }}
            data={{
              tasks: [
                {
                  id: -1,
                  text: project?.name,
                  type: 'project_bar',
                  status: 'todo',
                  start_date: projectStartDate,
                  end_date: projectForecastedEndDate,
                  scheduled_end_date: projectScheduledEndDate,
                  forecasted_start_date: projectStartDate,
                  forecasted_end_date: projectForecastedEndDate,
                  duration_string: differenceInDays(projectStartDate, projectForecastedEndDate),
                  cal_days: differenceInDays(projectForecastedEndDate, projectStartDate),
                  open: true,
                  readonly: true,
                  display_order: 0,
                  project_id: project.id,
                },
                ...tasks,
              ],
              links: dependencies,
            }}
            events={[
              ...taskGanttEvents,
              {
                onParse: function () {
                  this.batchUpdate(() => {
                    selectedCustomView?.parameters?.closedTask.forEach((id) => {
                      if (this.isTaskExists(id)) {
                        const task = this.getTask(id);
                        task.$open = false;
                        this.refreshTask(task.id);
                      }
                    });
                  });
                },
                onTaskCreated: function (task) {
                  if (task.type === gantt.config.types.placeholder) {
                    const count = gantt.getTaskCount();
                    task.text = 'New Task';
                    task.placeholder = true;
                    task.$wbs = -1;
                    task.display_order = count;
                  }
                  return true;
                },
              },
            ]}
            layers={[
              addWeekdaysLayer,
              drawFloatLayer,
              drawCompanyColorLayer,
              addForecastedDatesLayer,
            ]}
            plugins={{
              critical_path: false,
              grouping: true,
              export_api: true,
              tooltip: true,
            }}
          />
          <RecipeDrawer ganttId={ganttId} />
          <PlanningBoardTaskDrawer disableEdit={!canEdit} ganttId={ganttId} />
          <DisplayWithEditAccess>
            <BulkEditMenu ganttId={ganttId} projectId={projectId} />
          </DisplayWithEditAccess>
          {predecessorDialog.open && (
            <EditPredecessorDialog
              ganttId={ganttId}
              handleClose={handleClosePredecessorDialog}
              open={true}
              taskId={predecessorDialog.taskId}
            />
          )}
        </div>
      ) : (
        <Box
          alignItems={'center'}
          display={'flex'}
          height={'100%'}
          justifyContent={'center'}
          width={'100%'}
        >
          <Fade
            in={loading || reloadGantt}
            style={{
              transitionDelay: loading ? '300ms' : '0ms',
            }}
          >
            <CircularProgress />
          </Fade>
        </Box>
      )}
    </>
  );
};

export default PlanningGantt;
